import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { LocaleService, ContentZonesService } from '@sm360/widget-commons-core-services';
import { ImageWithFallback, LoaderAnim } from '@sm360/widget-commons-basic-components';
import { TileDisclaimer } from '@sm360/widget-commons-tiles';
import { PurchaseMethods, Vehicle } from '@sm360/widget-commons-vehicle';

import config from '../../../config/index.js';

import Badges from './Badges/Badges.jsx';

import './InventoryUsedTile.scss';

const { Payment: Financing, Price, TileTag, VehicleModel, URL } = Vehicle;
const { PurchaseMethodsValues: PURCHASE_METHODS } = PurchaseMethods;

function getFinanceTypeForDisclaimers(currentMethod, TileFinanceType) {
    switch (currentMethod) {
        case PURCHASE_METHODS.FINANCE:
            return TileFinanceType.FINANCE;
        case PURCHASE_METHODS.LEASE:
            return TileFinanceType.LEASE;
        case PURCHASE_METHODS.CASH:
            return TileFinanceType.CASH;
        default:
            return '';
    }
}

const InventoryUsedTile = ({
    certifiedText,
    certifiedBadge,
    defaultPicture,
    dealerMake,
    inspectedText,
    inspectedBadge,
    onClick,
    hasCarfaxBadge,
    tradedText,
    tradedBadge,
    vehicle,
    warrantyText,
    warrantyBadge,
    isLoading,
}) => {
    const flags = {
        comingSoon: 'COMING_SOON',
    };

    const { disclaimers } = vehicle.purchaseMethods;
    const taxesExcludedDisclaimer = disclaimers ? disclaimers.taxesExcluded : '';
    const taxCodes = vehicle.purchaseMethods.getTaxCodesString()
        ? vehicle.purchaseMethods.getTaxCodesString()
        : LocaleService.t('taxes.taxes');

    const clearance = !!vehicle.clearance && (
        <div className="car-clearance">{LocaleService.t('VEHICLE_STATUS.CLEARANCE')}</div>
    );

    const isComingSoon = vehicle.flags.reduce((resolver, { type }) => {
        return resolver || type === flags.comingSoon;
    }, false);

    const term = vehicle.purchaseMethods.getSelectedFinanceTerm();
    const save = vehicle.discount > 0 && (
        <div className="listing-tile-car-save has-warning-background">
            <div className="car-save">
                {LocaleService.t('SAVE_MONEY')}
                <span className="is-bold">
                    {LocaleService.t('VEHICLE_STATUS.PRICING', { price: Math.round(vehicle.discount) })}
                </span>
            </div>
        </div>
    );

    let paymentMethod;
    const currentMethod = vehicle.purchaseMethods.getSelectedPurchaseMethodValue();
    if (currentMethod === PURCHASE_METHODS.FINANCE) {
        paymentMethod = <Financing term={term} purchaseMethods={vehicle.purchaseMethods} taxCodes={taxCodes} isUsed />;
    } else if (currentMethod === PURCHASE_METHODS.CASH) {
        const includedDisclaimer = ContentZonesService.getContentZone(
            'widget.commons',
            'disclaimer.price.included.used.cash'
        )
            ? ContentZonesService.getContentZone('widget.commons', 'disclaimer.price.included.used.cash').getValue()
            : '';

        paymentMethod = (
            <Price
                amount={
                    vehicle.purchaseMethods.cashPurchase.getRetail() -
                    vehicle.purchaseMethods.cashPurchase.getTotalRebates()
                }
                taxesExcludedDisclaimer={taxesExcludedDisclaimer}
                includedDisclaimer={includedDisclaimer}
                retail={vehicle.purchaseMethods.cashPurchase.getRetail()}
                taxCodes={taxCodes}
            />
        );
    }

    const hasRetail = vehicle.purchaseMethods.cashPurchase.getRetail() > 0;
    const vehicleImage = vehicle.multimedia.getMainImage360x270() || defaultPicture;

    const getCZDisclaimer = () => {
        return ContentZonesService.getContentZone('widget.listing.used', 'disclaimer.used')
            ? ContentZonesService.getContentZone('widget.listing.used', 'disclaimer.used').getValue()
            : '';
    };

    const shouldDisplayOrigin = ContentZonesService.getContentZone('widget.listing.used', 'should.display.origin')
        ? ContentZonesService.getContentZone('widget.listing.used', 'should.display.origin').getValue() === 'true'
        : false;

    return (
        <div id={vehicle.vehicleId} className="listing-tile-wrapper">
            <div className="listing-tile" onClick={hasRetail ? onClick : ''}>
                <div className="listing-tile-image">
                    {save}
                    <div className="car-info">
                        <TileTag
                            shouldRender={vehicle.locked}
                            icon="icon-lock"
                            background="#F5273F"
                            color="#fff"
                            text={LocaleService.t('VEHICLE_STATUS.RESERVED')}
                        />
                        <TileTag shouldRender={isComingSoon} text={LocaleService.t('VEHICLE_STATUS.COMING_SOON')} />
                        <div className="car-meta">#{vehicle.stockNumber}</div>
                        <div className="car-name is-bold">{vehicle.getYearMakeModel()}</div>
                        <div className="car-meta">{vehicle.trim}</div>
                        <div className="car-tagline">{vehicle.tagLine}</div>
                    </div>
                    {clearance}
                    <div className="car-image-wrapper">
                        {!isLoading ? (
                            <ImageWithFallback
                                imageUrl={vehicleImage}
                                fallbackImageUrl={`${config.source.image}/ir/w360h270/images/${URL.NO_PHOTO}`}
                                className="car-image"
                                alt={vehicle.getYearMakeModel()}
                            />
                        ) : (
                            <LoaderAnim />
                        )}
                    </div>
                </div>
                <div className="listing-tile-drivePowerTrains">
                    <div className="listing-tile-drivetrain">
                        <icon className="picto-showroom icon-Front-Wheel-Drive" />
                        <p>{vehicle.driveTrain}</p>
                    </div>
                    <div className="listing-tile-transmission">
                        <icon className="picto-showroom icon-transmission" />
                        <p>{vehicle.transmission}</p>
                    </div>
                    <div className="listing-tile-km">
                        <icon className="picto-showroom icon-km" />
                        <p>{vehicle.getOdometer()}</p>
                    </div>
                </div>

                <div className="listing-tiles-payment">{paymentMethod}</div>
                <div className="listing-tile-footer">
                    <Badges
                        dealerMake={dealerMake}
                        hasCarfaxBadge={hasCarfaxBadge}
                        certifiedText={certifiedText}
                        inspectedText={inspectedText}
                        tradedText={tradedText}
                        warrantyText={warrantyText}
                        certifiedBadge={certifiedBadge}
                        inspectedBadge={inspectedBadge}
                        tradedBadge={tradedBadge}
                        warrantyBadge={warrantyBadge}
                        vehicle={vehicle}
                    />
                    {shouldDisplayOrigin &&
                        vehicle.organizationUnitName && (
                            <div className="listing-tile-origin is-light">
                                <icon className="icon-map" />
                                <p>
                                    {LocaleService.t('VEHICLE_TILE.ORIGIN', {
                                        organizationUnitName: vehicle.organizationUnitName,
                                    })}
                                </p>
                            </div>
                        )}
                </div>
            </div>
            <TileDisclaimer
                vehiclePurchaseMethod={vehicle.purchaseMethods}
                currentFinanceType={getFinanceTypeForDisclaimers(currentMethod, TileDisclaimer.FINANCE_TYPE)}
                czDisclaimer={getCZDisclaimer()}
            />
        </div>
    );
};

InventoryUsedTile.defaultProps = {
    certifiedText: '',
    inspectedText: '',
    tradedText: '',
    warrantyText: '',
    certifiedBadge: '',
    inspectedBadge: '',
    tradedBadge: '',
    warrantyBadge: '',
    defaultPicture: '',
    dealerMake: '',
    onClick: noop,
    hasCarfaxBadge: false,
};

InventoryUsedTile.propTypes = {
    defaultPicture: PropTypes.string,
    dealerMake: PropTypes.string,
    certifiedText: PropTypes.string,
    inspectedText: PropTypes.string,
    warrantyText: PropTypes.string,
    tradedText: PropTypes.string,
    certifiedBadge: PropTypes.string,
    inspectedBadge: PropTypes.string,
    warrantyBadge: PropTypes.string,
    tradedBadge: PropTypes.string,
    hasCarfaxBadge: PropTypes.bool,
    onClick: PropTypes.func,
    vehicle: PropTypes.instanceOf(VehicleModel).isRequired,
    isLoading: PropTypes.bool.isRequired,
};

InventoryUsedTile.displayName = 'Tile-A.InventoryUsedTile';

export default InventoryUsedTile;



// WEBPACK FOOTER //
// /builds/development/frontend/widgets/widgets/components/tiles/tile-a/src/components/Tile/InventoryUsed/InventoryUsedTile.jsx